import { CustomTheme } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { themedUrls } from '@/components/shared/components/ThemeManager/constants/themedUrls';
import { DEFAULT_THEME_NAME } from '@/components/shared/components/ThemeManager/ThemeManager';
import { ENV_DARK_MODE } from '@/config/envVars';
import { getTheme } from '@/config/themes/getTheme';
import { Theme } from '@/config/types';
import { useUserContext } from '@/contexts/UserContext/UserContext';

export function useCurrentThemeDefinition() {
  const { userEntity } = useUserContext();

  const [theme, setTheme] = useState<Partial<Theme> | undefined | null>(null);
  const [darkMode] = useState<boolean>(localStorage.getItem(ENV_DARK_MODE) === 'dark');

  const themeName = theme?.themeName;

  const getThemeByUrl = (): CustomTheme | undefined => {
    const currentUrl = window.location.href;
    const currentUrlCustomTheme = themedUrls
      .filter((themedUrl) => currentUrl.includes(themedUrl[0]))
      .map((themedUrl) => themedUrl[1])[0];
    return currentUrlCustomTheme;
  };

  const getThemeByUserEntity = async (): Promise<CustomTheme | undefined> => {
    if (!userEntity) return undefined;
    return await userEntity.getCustomTheme();
  };

  const setThemeFromThemeName = async (themeName: CustomTheme): Promise<void> => {
    const { theme } = await getTheme(themeName, darkMode);
    setTheme(theme);
  };

  useEffect(() => {
    const themeName = getThemeByUrl();
    if (themeName) {
      setThemeFromThemeName(themeName);
    }
  }, []);

  useEffect(() => {
    if (theme || !userEntity) return;
    (async () => {
      const themeName = (await getThemeByUserEntity()) ?? DEFAULT_THEME_NAME;
      setThemeFromThemeName(themeName);
    })();
  }, [userEntity]);

  const configZendeskCustomerSupport = () => {
    const dataLayer = window.dataLayer || [];
    if (theme && theme.themeName && theme.zendeskCustomerSupport) {
      const themeName = theme.themeName;
      dataLayer.push({
        event: 'zendesk_theme',
        themeName: CustomTheme[themeName]?.themeName,
        color: {
          theme: CustomTheme[themeName]?.palette?.secondary?.main,
          launcher: CustomTheme[themeName]?.palette?.secondary?.contrastText,
          launcherText: CustomTheme[themeName]?.palette?.secondary?.main,
          button: CustomTheme[themeName]?.palette?.secondary?.main,
          resultLists: CustomTheme[themeName]?.palette?.secondary?.main,
          header: CustomTheme[themeName]?.palette?.secondary?.main,
          articleLinks: CustomTheme[themeName]?.palette?.secondary?.main,
        },
      });
      dataLayer.push({
        event: 'customerSupport',
        customerSupport: CustomTheme[themeName]?.zendeskCustomerSupport,
      });
    }
  };

  useEffect(() => {
    if (theme?.zendeskCustomerSupport) configZendeskCustomerSupport();
  }, [theme]);

  return { themeName, theme };
}
